import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import { useApolloClient, useQuery } from "react-apollo";
import { Control, Button, Label, Input } from "rbx";
import { generateOrderDispatchingColumns } from "../columns";
import {
  ourToast as toast,
  getFilterState,
  saveFilterState,
  runPromisesInSeries
} from "../../../../utils";
import {
  UPDATE_ORDER_MUTATION,
  ALL_ORDERS_VIEW_QUERY
} from "../../../../graphql/Orders/Orders.graphql";
import { ALL_SYSTEM_CODES_QUERY } from "../../../../graphql/SystemCodes/SystemCodes.graphql";
import Loading from "../../../../components/Loading";
import { GroupedOrdersTable } from "../../../../components/Orders/OrdersTable";

import { transformOrders } from "../eld.utils";

class BookedError extends Error {}

const ELDBookedOrdersPage = () => {
  const client = useApolloClient();
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order", "ELD"],
        CodeName_in: ["OrderStatus", "ELDStatus"],
        CodeId_in: ["BK-E", "EXP", "EX"]
      }
    }
  });
  const [orders, setOrders] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() =>
    getFilterState("BOOKED_ORDERS_FILTER")
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { RecId } = SystemCodes?.data?.SystemCodes.find(
        s => s.CodeId === "BK-E"
      );
      const where = {
        StatusRecIdRef: RecId
      };

      if (filters.PickupDate) {
        where.TripKey_startsWith = `${moment(filters.PickupDate).format(
          "YYYY-MM-DD"
        )}`;
      }
      const {
        data: { VW_Orders = [] }
      } = await client.query({
        query: ALL_ORDERS_VIEW_QUERY,
        variables: {
          where
        },
        fetchPolicy: "network-only"
      });
      setOrders(VW_Orders);
      setData(transformOrders(VW_Orders));
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }, [SystemCodes, client, filters]);

  useEffect(() => {
    if (SystemCodes?.data?.SystemCodes?.length && !data) {
      fetchData();
    }
  }, [SystemCodes, client, data, fetchData, orders]);

  const handleExportSelected = async selectedData => {
    try {
      if (!selectedData.length) {
        throw BookedError("Please select some orders to be exported");
      }
      setLoading(true);
      const ELDStatus = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "EX"
      );
      const Status = SystemCodes.data.SystemCodes.find(s => s.CodeId === "EXP");
      const dataToExport = [];
      selectedData.forEach(s => {
        s.forEach(t => dataToExport.push(t));
      });

      await runPromisesInSeries(
        dataToExport.map(({ RecId }) => () =>
          client.mutate({
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              data: {
                StatusRecIdRef: Status.RecId,
                ELDStatusRecIdRef: ELDStatus.RecId,
                DateExported: new Date()
              },
              where: {
                RecId
              }
            }
          })
        )
      );

      setLoading(false);
      toast.success("Orders exported successfully");
      fetchData();
    } catch (err) {
      if (err instanceof BookedError) {
        toast.error(err.message);
      } else {
        toast.error("Error exporting Orders.");
      }
    }
  };

  const ORDER_DISPATCHING_COLUMNS = useMemo(() => {
    if (SystemCodes.data) {
      return generateOrderDispatchingColumns();
    }
  }, [SystemCodes]);

  const handleChangeFn = e => {
    if (e.target.value) {
      const currState = {
        ...filters,
        PickupDate: e.target.value
      };
      saveFilterState("BOOKED_ORDERS_FILTER", currState);
      setFilters(currState);
    } else {
      const currState = {};
      setFilters(currState);
      saveFilterState("BOOKED_ORDERS_FILTER", currState);
    }
  };

  if (SystemCodes.loading || loading) return <Loading />;

  return (
    <div>
      <header className="page-head">
        <form
          className="page-head-start"
          id="filter-form"
          onSubmit={e => {
            e.preventDefault();
            fetchData();
          }}
        >
          <Control>
            <Label>Pickup Date</Label>
            <Input
              name="PickupDate_gt"
              type="date"
              value={filters.PickupDate}
              onChange={handleChangeFn}
            />
          </Control>
          <Control style={{ marginTop: "1.5rem" }}>
            <Button color="link" form="filter-form">
              Filter
            </Button>
          </Control>
        </form>
      </header>
      {SystemCodes?.data?.SystemCodes?.length > 0 && data && (
        <React.Fragment>
          <GroupedOrdersTable
            callToAction1={handleExportSelected}
            columns={ORDER_DISPATCHING_COLUMNS}
            data={data}
            sortBy={[
              { id: "PickupDate", desc: false },
              { id: "Trip", desc: false }
            ]}
            onCellClick={() => {}}
          />
        </React.Fragment>
      )}
    </div>
  );
};
ELDBookedOrdersPage.propTypes = {};
export default ELDBookedOrdersPage;
